import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StarRating } from "@/components/StarRating";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Star } from "lucide-react";

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  is_verified: boolean;
  reviewer: {
    first_name: string | null;
  } | null;
  service: {
    title: string;
  } | null;
}

export function MyReviewsSection() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);

  useEffect(() => {
    if (!user) return;

    const fetchReviews = async () => {
      setLoading(true);
      try {
        // Fetch reviews where the user is the provider
        const { data, error } = await supabase
          .from('reviews')
          .select(`
            id,
            rating,
            comment,
            created_at,
            is_verified,
            service_id
          `)
          .eq('provider_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Get reviewer profiles and service titles
        const reviewsWithDetails: Review[] = [];
        for (const review of data || []) {
          // Get reviewer profile using RPC for security
          const { data: reviewerData } = await supabase
            .rpc('get_provider_reviews', { target_provider_id: user.id });

          // Get service title
          const { data: serviceData } = await supabase
            .from('services')
            .select('title')
            .eq('id', review.service_id)
            .single();

          const matchingReview = reviewerData?.find((r: any) => r.id === review.id);

          reviewsWithDetails.push({
            ...review,
            reviewer: {
              first_name: matchingReview?.reviewer_first_name || 'Anonymous',
            },
            service: serviceData || null,
          });
        }

        setReviews(reviewsWithDetails);

        // Calculate average
        if (reviewsWithDetails.length > 0) {
          const sum = reviewsWithDetails.reduce((acc, r) => acc + r.rating, 0);
          setAverageRating(sum / reviewsWithDetails.length);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [user]);

  if (loading) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-poppins text-2xl flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> My Reviews
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-poppins text-2xl flex items-center gap-2">
            <MessageSquare className="h-5 w-5" /> My Reviews
          </CardTitle>
          {reviews.length > 0 && (
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
              <span className="font-bold text-lg">{averageRating.toFixed(1)}</span>
              <span className="text-muted-foreground">({reviews.length} reviews)</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {reviews.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No reviews yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Complete services to start receiving reviews from clients
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="border border-border rounded-lg p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StarRating value={review.rating} readOnly size="sm" />
                    {review.is_verified && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        Verified
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  From {review.reviewer?.first_name || 'Anonymous'} for{' '}
                  <span className="font-medium text-foreground">
                    {review.service?.title || 'Unknown Service'}
                  </span>
                </p>
                {review.comment && (
                  <p className="text-sm mt-2">{review.comment}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
